import { FC, useState } from 'react';

import { AddDocumentIcon } from '../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '../../../../components/hover-card/index';
import { PaginationController } from '../../../../components/pagination-controller/index';
import { Company, EventHooks, useFormat } from '../../../../hooks/index';
import {
  CompanyIncorporationEventItem,
  eventFormType,
  EventTypeBackend,
} from '../../../../types/events.types';
import { getS3FileOriginalName } from '../../../../utils/get-s3file-original-name.util';
import { toNumber } from '../../../../utils/to-number.util';
import { resolveTransactionDisplayDate } from '../../utils/resolve-transaction-display-date.util';
import { CardProps } from './transaction-card-variants.types';
import { TransactionOverviewItem } from './transaction-overview-item';
import { TransactionTitleItem } from './transaction-title-item';

export const CompanyIncorporation: FC<CardProps> = ({ event, companyId, openFileUploadModal }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const { format } = useFormat();
  const { selectedCompany: { currency } = {} } = Company.useSelected();
  const { items, totalPages } = EventHooks.useTransactions<CompanyIncorporationEventItem>({
    eventId: event.id,
    eventType: EventTypeBackend.COMPANY_INCORPORATION,
    companyId,
    currentPage,
  });

  if (event.type !== EventTypeBackend.COMPANY_INCORPORATION) return <></>;

  const { type, filesLinks, parValue } = event;
  const parsedParValue = toNumber(parValue) ?? 0;
  const currencySymbol = currency?.symbol ?? '$';

  const formatCurrency = (value: number) =>
    `${currencySymbol}${value.toLocaleString('en-US', { maximumFractionDigits: 7 })}`;

  return (
    <>
      {items.map(
        (
          {
            date,
            shareClass,
            sharesCount,
            stakeholder,
            filesLinks: itemFilesLinks,
            reverseVesting,
          },
          i,
        ) => {
          const considerationPay = sharesCount * parsedParValue;
          const documentLinks = itemFilesLinks.length > 0 ? itemFilesLinks : filesLinks;

          return (
            <div
              className="flex flex-col divide-y divide-gray-100 rounded-md shadow-sm"
              key={i + shareClass.id}
            >
              <TransactionTitleItem
                createdAt={format(
                  resolveTransactionDisplayDate({ date, reverseVesting }),
                  'dd/MM/yyyy',
                )}
                name={stakeholder?.fullName}
              />
              <TransactionOverviewItem
                title={<AppFormattedMessage id="TRANSACTION_TYPE" />}
                value={<AppFormattedMessage id={eventFormType[type]} />}
              />
              <TransactionOverviewItem
                title={<AppFormattedMessage id="SHARE_CLASS" />}
                value={shareClass.name}
              />
              <TransactionOverviewItem
                title={<AppFormattedMessage id="SHARES_ISSUANCE" />}
                value={sharesCount.toLocaleString('en-US')}
              />
              <TransactionOverviewItem
                title={<AppFormattedMessage id="SHARE_PAR_VALUE" />}
                value={formatCurrency(parsedParValue)}
              />
              <TransactionOverviewItem
                title={<AppFormattedMessage id="CONSIDERED_PAY" />}
                value={formatCurrency(considerationPay)}
              />
              {documentLinks.length > 0 && (
                <TransactionOverviewItem
                  title={<AppFormattedMessage id="DOCUMENTS" />}
                  value={
                    <HoverCard>
                      <HoverCardTrigger onClick={() => openFileUploadModal(documentLinks)}>
                        <AddDocumentIcon className="cursor-pointer" />
                      </HoverCardTrigger>

                      <HoverCardContent
                        className="max-w-[360px] rounded-lg bg-[#101828] px-3 py-2"
                        side="top"
                      >
                        <span className="block w-full truncate p-0 text-label-md font-[550] text-white">
                          {documentLinks.length === 1
                            ? getS3FileOriginalName(documentLinks[0])
                            : `Download ${documentLinks.length} files`}
                        </span>
                      </HoverCardContent>
                    </HoverCard>
                  }
                />
              )}
            </div>
          );
        },
      )}
      <PaginationController
        className="mt-6 shrink-0"
        currentPage={currentPage}
        onClick={setCurrentPage}
        totalPages={totalPages}
      />
    </>
  );
};
